import { Component, OnInit, signal, computed } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ClientService } from '../../../../service/client.service';
import { ClientDetails } from '../../../models/client.model';
import { Button, ButtonDirective } from 'primeng/button';
import { PageLayoutComponent } from '../../../shared/page-layout/page-layout';
import { DatePipe } from '@angular/common';
import { InputText } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Checkbox } from 'primeng/checkbox';
import { Select } from 'primeng/select';
import { StateContainerComponent, ViewState } from '../../../shared/state-container/state-container.component';

@Component({
  selector: 'app-client-detail',
  templateUrl: './client-detail.html',
  imports: [
    ReactiveFormsModule,
    Button,
    PageLayoutComponent,
    DatePipe,
    FormsModule,
    ButtonDirective,
    InputText,
    ToastModule,
    Checkbox,
    Select,
    StateContainerComponent
  ],
  providers: [MessageService],
})
export class ClientDetail implements OnInit {

  client = signal<ClientDetails | null>(null);
  loading = signal(false);
  error = signal<any>(null);
  isEditMode = signal(false);
  isSaving = signal(false);
  authorizationGrantTypeOptions = signal<string[]>([]);
  ttlUnits = [
    { label: 'Minutes', value: 'minutes' },
    { label: 'Hours', value: 'hours' },
    { label: 'Days', value: 'days' }
  ];

  form!: FormGroup;

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (!this.client()) return 'empty';
    return 'content';
  });

  readonly emptyStateMessage = computed(() => {
    return 'Client not found or does not exist.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load client details. Please try again.';
  });

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private clientService: ClientService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id')!;
    this.loadClient(id);
    this.loadAuthorizationGrantTypes();
  }

  loadAuthorizationGrantTypes() {
    this.clientService.getAuthorizationGrantTypes().subscribe({
      next: (types) => {
        this.authorizationGrantTypeOptions.set(types);
      },
      error: () => {
        console.error('Failed to load authorization grant types');
      }
    });
  }

  get c(): ClientDetails | null {
    return this.client();
  }

  loadClient(id: string) {
    this.loading.set(true);
    this.error.set(null);

    this.clientService.getClientById(id).subscribe({
      next: (res) => {
        this.client.set(res);
        this.initForm(res);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err);
        this.loading.set(false);
      }
    });
  }

  // Retry method for error state
  retryLoad() {
    const id = this.route.snapshot.paramMap.get('id')!;
    this.loadClient(id);
  }

  initForm(data: ClientDetails) {

    const parseISOToDuration = (iso: string) => {
      if (!iso) return { value: 1, unit: 'hours' };

      const value = parseInt(iso.match(/\d+/)?.[0] || '1');

      if (iso.includes('D')) return { value, unit: 'days' };
      if (iso.includes('H')) return { value, unit: 'hours' };
      if (iso.includes('M')) return { value, unit: 'minutes' };

      return { value: 1, unit: 'hours' };
    };

    this.form = this.fb.group({
      description: [data.description || ''],

      scopes: this.fb.array((data.scopes || []).map(s => new FormControl(s))),
      redirectUris: this.fb.array((data.redirectUris || []).map(u => new FormControl(u))),
      authorizationGrantTypes: this.fb.array((data.authorizationGrantTypes || []).map(g => new FormControl(g))),
      postLogoutRedirectUris: this.fb.array((data.postLogoutRedirectUris || []).map(u => new FormControl(u))),

      // ✅ UPDATED TTL STRUCTURE
      accessTokenTTL: this.fb.group(
        parseISOToDuration(data.tokenSettings?.accessTokenTimeToLive || '')
      ),
      refreshTokenTTL: this.fb.group(
        parseISOToDuration(data.tokenSettings?.refreshTokenTimeToLive || '')
      ),

      reuseRefreshTokens: [data.tokenSettings?.reuseRefreshTokens || false],
    });
  }

  get scopes(): FormArray<FormControl> {
    return this.form.get('scopes') as FormArray<FormControl>;
  }

  get redirectUris(): FormArray<FormControl> {
    return this.form.get('redirectUris') as FormArray<FormControl>;
  }

  get authorizationGrantTypes(): FormArray<FormControl> {
    return this.form.get('authorizationGrantTypes') as FormArray<FormControl>;
  }

  get postLogoutRedirectUris(): FormArray<FormControl> {
    return this.form.get('postLogoutRedirectUris') as FormArray<FormControl>;
  }

  isGrantTypeSelected(grantType: string): boolean {
    const grantTypes = this.authorizationGrantTypes.value;
    return grantTypes.includes(grantType);
  }

  toggleGrantType(grantType: string): void {
    const grantTypes = this.authorizationGrantTypes;
    const index = grantTypes.value.indexOf(grantType);

    if (index > -1) {
      // Remove if already selected
      grantTypes.removeAt(index);
    } else {
      // Add if not selected
      grantTypes.push(new FormControl(grantType));
    }

    this.form.markAsDirty();
  }

  addItem(type: 'scopes' | 'redirectUris' | 'authorizationGrantTypes' | 'postLogoutRedirectUris') {
    const control = new FormControl('');

    if (type === 'scopes') this.scopes.push(control);
    if (type === 'redirectUris') this.redirectUris.push(control);
    if (type === 'authorizationGrantTypes') this.authorizationGrantTypes.push(control);
    if (type === 'postLogoutRedirectUris') this.postLogoutRedirectUris.push(control);
  }

  deleteItem(type: 'scopes' | 'redirectUris' | 'authorizationGrantTypes' | 'postLogoutRedirectUris', index: number) {
    if (type === 'scopes') this.scopes.removeAt(index);
    if (type === 'redirectUris') this.redirectUris.removeAt(index);
    if (type === 'authorizationGrantTypes') this.authorizationGrantTypes.removeAt(index);
    if (type === 'postLogoutRedirectUris') this.postLogoutRedirectUris.removeAt(index);

    // Mark form as dirty so save button gets enabled
    this.form.markAsDirty();
  }

  isFormDirty(): boolean {
    return this.form.dirty;
  }

  save() {

    const durationToISO = (value: number, unit: string): string => {
      if (!value) return 'PT0S';

      switch (unit) {
        case 'minutes':
          return `PT${value}M`;
        case 'hours':
          return `PT${value}H`;
        case 'days':
          return `P${value}D`;
        default:
          return 'PT0S';
      }
    };

    const updated = {
      ...this.client(),
      ...this.form.value,

      tokenSettings: {
        accessTokenTimeToLive: durationToISO(
          this.form.value.accessTokenTTL.value,
          this.form.value.accessTokenTTL.unit
        ),
        refreshTokenTimeToLive: durationToISO(
          this.form.value.refreshTokenTTL.value,
          this.form.value.refreshTokenTTL.unit
        ),
        reuseRefreshTokens: this.form.value.reuseRefreshTokens,
      }
    };

    this.isSaving.set(true);

    this.clientService.updateClient(updated.id, updated).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Client updated successfully',
          life: 3000
        });

        this.isSaving.set(false);
        this.loadClient(updated.id);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update client',
          life: 3000
        });

        this.isSaving.set(false);
      }
    });
  }
}